import { useState, useContext, ReactNode } from 'react';
import { Box, Typography, Button, alpha } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { palette } from '../theme/colors';
import { UpgradeModal } from './UpgradeModal';
import { SubscriptionTier, useTierAccess } from '../hooks/useTierAccess';

interface BlurredFeatureOverlayProps {
  children: ReactNode;
  feature: string;
  requiredTier?: SubscriptionTier;
  blurAmount?: number;
  showLock?: boolean;
  minHeight?: string | number;
}

/**
 * Wrapper component that blurs its children and shows an upgrade prompt
 * when the user doesn't have access to the required tier.
 *
 * Usage:
 * ```tsx
 * <BlurredFeatureOverlay feature="Dashboards" requiredTier="professional">
 *   <DashboardContent />
 * </BlurredFeatureOverlay>
 * ```
 */
export const BlurredFeatureOverlay = ({
  children,
  feature,
  requiredTier = 'professional',
  blurAmount = 6,
  showLock = true,
  minHeight = 200,
}: BlurredFeatureOverlayProps) => {
  const { mode } = useContext(ColorModeContext);
  const { tier, hasTierAccess } = useTierAccess();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const hasAccess = hasTierAccess(requiredTier);

  const colors = mode === 'dark' ? {
    overlay: alpha(palette.background, 0.7),
    accent: palette.accent,
    textPrimary: palette.textPrimary,
  } : {
    overlay: alpha(palette.light.background, 0.7),
    accent: palette.accent,
    textPrimary: palette.light.textPrimary,
  };

  // If user has access, just render children normally
  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          minHeight,
          overflow: 'hidden',
          borderRadius: 1,
        }}
      >
        {/* Blurred content */}
        <Box
          sx={{
            filter: `blur(${blurAmount}px)`,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {children}
        </Box>

        {/* Overlay with upgrade prompt */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: colors.overlay,
            backdropFilter: 'blur(2px)',
            borderRadius: 1,
            gap: 2,
            p: 3,
            cursor: 'pointer',
          }}
          onClick={() => setUpgradeModalOpen(true)}
        >
          {showLock && (
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: alpha(colors.accent, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LockIcon sx={{ fontSize: 32, color: colors.accent }} />
            </Box>
          )}

          <Typography variant="h6" color={colors.textPrimary} textAlign="center">
            {feature}
          </Typography>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            This feature requires a {requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1)} subscription
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={<RocketLaunchIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setUpgradeModalOpen(true);
            }}
            sx={{ mt: 1 }}
          >
            Upgrade to {requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1)}
          </Button>
        </Box>
      </Box>

      <UpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        feature={feature}
        requiredTier={requiredTier}
        currentTier={tier}
      />
    </>
  );
};

export default BlurredFeatureOverlay;
